import type { GenericAttributeMapping } from '../../../components/AttributeMapping';

import { escapeForJSON } from './placeholderUtils';

interface MappingReplacementContext {
  template: string;
  entitlementIndex: number;
  entitlementName: string;
  mappings: GenericAttributeMapping[];
  prompt?: string;
}

/**
 * Replace @MAPPINGJSON@, @ENTTYPE@, @ENTITLEMENTINDEX@, and @CUAPROMPT@ placeholders in template
 */
export const replaceMappingsAndEntitlements = (
  context: MappingReplacementContext
): string => {
  const { template, entitlementIndex, entitlementName, mappings, prompt } =
    context;

  // Build mapping object from mappings array
  const mappingObject = mappings.reduce<Record<string, string>>(
    (acc, mapping) => {
      if (mapping.saviyntField && mapping.externalAppField) {
        acc[String(mapping.externalAppField)] = `\${${String(
          mapping.saviyntField
        )}}`;
      }

      return acc;
    },
    {}
  );

  // Convert to JSON string without outer braces, handle empty mappings
  // Don't double-escape quotes - they're already properly formatted for JSON context
  const mappingJson =
    Object.keys(mappingObject).length > 0
      ? JSON.stringify(mappingObject).slice(1, -1)
      : '';

  // Use prompt if provided, otherwise keep @CUAPROMPT@ placeholder
  let cuaPromptValue = prompt && prompt.trim() ? prompt.trim() : '@CUAPROMPT@';

  cuaPromptValue = escapeForJSON(cuaPromptValue);

  // Replace all placeholders in template
  return template
    .replace('@MAPPINGJSON@', mappingJson)
    .replace(/@ENTTYPE@/g, entitlementName)
    .replace(/@ENTITLEMENTINDEX@/g, entitlementIndex.toString())
    .replace(/@CUAPROMPT@/g, cuaPromptValue);
};

/**
 * Build payload with mappings for multiple entitlements
 * Used by: hybridProcessingUtils, buildRemoveAccessPayloadFromRedux
 */
export const buildPayloadWithMappings = (
  template: string,
  entitlements: { value: string }[],
  allMappings: Record<number, GenericAttributeMapping[]>,
  jsonKey = 'AddAccessJSON'
): Record<string, unknown> => {
  // For single entitlement
  if (entitlements.length === 1) {
    const mappings = allMappings[0] || [];
    const transformedTemplate = replaceMappingsAndEntitlements({
      template,
      entitlementIndex: 0,
      entitlementName: entitlements[0].value,
      mappings,
    });

    return {
      multipleconnection: false,
      [`externalconnattvalue.${jsonKey}`]: transformedTemplate,
    };
  }

  // For multiple entitlements - build call array
  try {
    const baseTemplate = JSON.parse(template) as { call?: unknown[] };

    if (
      !baseTemplate.call ||
      !Array.isArray(baseTemplate.call) ||
      baseTemplate.call.length === 0
    ) {
      throw new Error('Invalid template structure');
    }

    const baseEntry = baseTemplate.call[0] as Record<string, unknown>;
    const callArray: unknown[] = [];

    entitlements.forEach((entitlement, index) => {
      const entryForThisEntitlement = JSON.parse(JSON.stringify(baseEntry)) as {
        name?: string;
        httpParams?: string;
      };
      const mappings = allMappings[index] || [];

      // Set entry name
      entryForThisEntitlement.name = entitlement.value;

      // Replace placeholders in httpParams
      if (typeof entryForThisEntitlement.httpParams === 'string') {
        entryForThisEntitlement.httpParams = replaceMappingsAndEntitlements({
          template: entryForThisEntitlement.httpParams,
          entitlementIndex: index,
          entitlementName: entitlement.value,
          mappings,
        });
      }

      callArray.push(entryForThisEntitlement);
    });

    return {
      multipleconnection: false,
      [`externalconnattvalue.${jsonKey}`]: JSON.stringify({ call: callArray }),
    };
  } catch (error) {
    console.error('Error processing template:', error);

    return {
      multipleconnection: false,
      [`externalconnattvalue.${jsonKey}`]: template,
    };
  }
};

/**
 * Unified payload building function for AddAccess/RemoveAccess using Redux data
 * Works for both create and edit modes - always cumulative
 * Used by: buildCumulativePayload, buildRemoveAccessCumulativePayload
 */
export const buildUnifiedAccessPayload = (
  template: string,
  entitlements: { value: string }[],
  stepFormData: Record<string, unknown>,
  prompts: Record<number, string> = {},
  jsonKey: 'AddAccessJSON' | 'RemoveAccessJSON' = 'AddAccessJSON'
): Record<string, unknown> => {
  const stepPrefix = jsonKey === 'AddAccessJSON' ? 'AddAccess' : 'RemoveAccess';

  // Extract all mappings and prompts from Redux
  const allMappings: Record<number, GenericAttributeMapping[]> = {};

  entitlements.forEach((_, index) => {
    const stepData = stepFormData[`${stepPrefix}_${index}`] as
      | { mappings?: GenericAttributeMapping[] }
      | undefined;

    if (stepData?.mappings) {
      allMappings[index] = stepData.mappings;
    }
  });

  // For single entitlement - use same JSON parsing logic as multiple entitlements
  if (entitlements.length === 1) {
    const mappings = allMappings[0] || [];
    const prompt = prompts[0] || '';

    try {
      const baseTemplate = JSON.parse(template) as { call?: unknown[] };

      if (!baseTemplate.call || !Array.isArray(baseTemplate.call)) {
        throw new Error('Invalid template structure');
      }

      const baseEntry = baseTemplate.call[0] as Record<string, unknown>;
      const entryForThisEntitlement = JSON.parse(JSON.stringify(baseEntry)) as {
        name?: string;
        httpParams?: string;
      };

      // Set entry name
      entryForThisEntitlement.name = entitlements[0].value;

      // Replace placeholders in httpParams
      if (typeof entryForThisEntitlement.httpParams === 'string') {
        entryForThisEntitlement.httpParams = replaceMappingsAndEntitlements({
          template: entryForThisEntitlement.httpParams,
          entitlementIndex: 0,
          entitlementName: entitlements[0].value,
          mappings,
          prompt,
        });
      }

      const finalTemplate = {
        ...baseTemplate,
        call: [entryForThisEntitlement],
      };

      return {
        multipleconnection: false,
        [`externalconnattvalue.${jsonKey}`]: JSON.stringify(finalTemplate),
      };
    } catch (error) {
      console.error('Error parsing template for single entitlement:', error);

      // Fallback to old method if JSON parsing fails
      const transformedTemplate = replaceMappingsAndEntitlements({
        template,
        entitlementIndex: 0,
        entitlementName: entitlements[0].value,
        mappings,
        prompt,
      });

      return {
        multipleconnection: false,
        [`externalconnattvalue.${jsonKey}`]: transformedTemplate,
      };
    }
  }

  // For multiple entitlements - build call array
  try {
    const baseTemplate = JSON.parse(template) as { call?: unknown[] };

    if (!baseTemplate.call || !Array.isArray(baseTemplate.call)) {
      throw new Error('Invalid template structure');
    }

    const baseEntry = baseTemplate.call[0] as Record<string, unknown>;
    const callArray: unknown[] = [];

    entitlements.forEach((entitlement, index) => {
      const entryForThisEntitlement = JSON.parse(JSON.stringify(baseEntry)) as {
        name?: string;
        httpParams?: string;
      };
      const mappings = allMappings[index] || [];
      const prompt = prompts[index] || '';

      // Set entry name
      entryForThisEntitlement.name = entitlement.value;

      // Replace placeholders in httpParams
      if (typeof entryForThisEntitlement.httpParams === 'string') {
        entryForThisEntitlement.httpParams = replaceMappingsAndEntitlements({
          template: entryForThisEntitlement.httpParams,
          entitlementIndex: index,
          entitlementName: entitlement.value,
          mappings,
          prompt,
        });
      }

      callArray.push(entryForThisEntitlement);
    });

    return {
      multipleconnection: false,
      [`externalconnattvalue.${jsonKey}`]: JSON.stringify({ call: callArray }),
    };
  } catch (error) {
    console.error('Error processing template:', error);

    return {
      multipleconnection: false,
      [`externalconnattvalue.${jsonKey}`]: template,
    };
  }
};

/**
 * Build cumulative payload for AddAccess (wrapper for unified function)
 * Used by: addAccessUtils, addAccessAttributeMappingLogic
 */
export const buildCumulativePayload = (
  template: string,
  entitlements: { value: string }[],
  stepFormData: Record<string, unknown>,
  currentEntitlementIndex: number,
  allPrompts: Record<number, string> = {}
): Record<string, unknown> =>
  buildUnifiedAccessPayload(
    template,
    entitlements,
    stepFormData,
    allPrompts,
    'AddAccessJSON'
  );

/**
 * Build cumulative payload for RemoveAccess (wrapper for unified function)
 * Used by: removeAccessUtils, removeAccessLogic
 */
export const buildRemoveAccessCumulativePayload = (
  template: string,
  entitlements: { value: string }[],
  stepFormData: Record<string, unknown>,
  currentEntitlementIndex: number,
  allPrompts: Record<number, string> = {}
): Record<string, unknown> =>
  buildUnifiedAccessPayload(
    template,
    entitlements,
    stepFormData,
    allPrompts,
    'RemoveAccessJSON'
  );

/**
 * Build RemoveAccess payload from Redux (used by removeAccessUtils)
 */
export const buildRemoveAccessPayloadFromRedux = (
  template: string,
  entitlements: { value: string }[],
  stepFormData: Record<string, unknown>,
  allPrompts: Record<number, string> = {}
): Record<string, unknown> =>
  buildUnifiedAccessPayload(
    template,
    entitlements,
    stepFormData,
    allPrompts,
    'RemoveAccessJSON'
  );
